import React, { useState } from "react";
import Swal from "sweetalert2";
import { useForm } from "../../hooks/useForm";
import {
	descargaExpedicionPorCicloYCultivo,
	descargaAlgodonLaboresPendientes,
	descargaPadronCultivos,
	descargaProductoresIncumplidos,
	descargaPermisosPozos,
	descargaPadronAlfalfaNueva,
	getPermisosCanceladosPorModulo,
	getPermisosFugitivos,
} from "../../helpers/DB/editarPermisos";

const CICLOS = ["2026-2027", "2025-2026", "2024-2025", "2023-2024", "2022-2023", "2021-2022"];

const OPERACIONES = [
	{
		key: "expedicionPorCicloYCultivo",
		nombre: "Expedición por ciclo y cultivo",
		tipo: "excel",
		campos: [
			{ name: "ciclo", label: "Ciclo", tipo: "ciclo" },
			{ name: "claveCultivo", label: "Clave de cultivo (ver Padrón de Cultivos)", tipo: "number" },
			{ name: "titulo", label: "Título del reporte", tipo: "text" },
		],
		ejecutar: ({ ciclo, claveCultivo, titulo }) =>
			descargaExpedicionPorCicloYCultivo(ciclo, Number(claveCultivo), titulo),
	},
	{
		key: "algodonLaboresPendientes",
		nombre: "Algodón con labores pendientes",
		tipo: "excel",
		campos: [{ name: "ciclo", label: "Ciclo", tipo: "ciclo" }],
		ejecutar: ({ ciclo }) => descargaAlgodonLaboresPendientes(ciclo),
	},
	{
		key: "padronCultivos",
		nombre: "Padrón de cultivos",
		tipo: "excel",
		campos: [],
		ejecutar: () => descargaPadronCultivos(),
	},
	{
		key: "productoresIncumplidos",
		nombre: "Productores incumplidos",
		tipo: "excel",
		campos: [],
		ejecutar: () => descargaProductoresIncumplidos(),
	},
	{
		key: "permisosPozos",
		nombre: "Permisos de pozos",
		tipo: "excel",
		campos: [{ name: "ciclo", label: "Ciclo", tipo: "ciclo" }],
		ejecutar: ({ ciclo }) => descargaPermisosPozos(ciclo),
	},
	{
		key: "padronAlfalfaNueva",
		nombre: "Padrón de alfalfa nueva",
		tipo: "excel",
		campos: [{ name: "ciclo", label: "Ciclo", tipo: "ciclo" }],
		ejecutar: ({ ciclo }) => descargaPadronAlfalfaNueva(ciclo),
	},
	{
		key: "permisosCanceladosPorModulo",
		nombre: "Permisos cancelados por módulo",
		tipo: "tabla",
		campos: [
			{ name: "ciclo", label: "Ciclo", tipo: "ciclo" },
			{ name: "modulo", label: "Módulo (ej. 14)", tipo: "text" },
		],
		ejecutar: ({ ciclo, modulo }) => getPermisosCanceladosPorModulo(ciclo, modulo),
	},
	{
		key: "permisosFugitivos",
		nombre: "Permisos que evadieron labores (fugitivos)",
		tipo: "tabla",
		campos: [
			{ name: "cicloAnterior", label: "Ciclo anterior", tipo: "ciclo" },
			{ name: "cicloActual", label: "Ciclo actual", tipo: "ciclo" },
		],
		ejecutar: ({ cicloAnterior, cicloActual }) => getPermisosFugitivos(cicloAnterior, cicloActual),
	},
];

export const HerramientasReportes = () => {
	const [herramientaKey, setHerramientaKey] = useState("");
	const [formValues, handleInputChange, reset] = useForm();
	const [loading, setLoading] = useState(false);
	const [resultado, setResultado] = useState(null);

	const herramienta = OPERACIONES.find((op) => op.key === herramientaKey);
	const camposCompletos = herramienta
		? herramienta.campos.every((campo) => formValues[campo.name] && formValues[campo.name] !== "")
		: false;

	const handleSeleccionar = ({ target }) => {
		setHerramientaKey(target.value);
		reset();
		setResultado(null);
	};

	const handleEjecutar = async () => {
		if (!herramienta || !camposCompletos) return;

		setLoading(true);
		setResultado(null);

		try {
			const salida = await herramienta.ejecutar(formValues);

			if (herramienta.tipo === "tabla") {
				setResultado(salida);
				if (salida.length === 0) {
					Swal.fire("Sin resultados", "No se encontraron registros.", "info");
				}
			} else {
				Swal.fire("Listo", `Se exportaron ${salida} registros a Excel.`, "success");
			}
		} catch (error) {
			console.error(error);
			Swal.fire("Error", error.message || "Ocurrió un error al ejecutar la herramienta.", "error");
		} finally {
			setLoading(false);
		}
	};

	return (
		<div className="col-12">
			<div className="row mb-3">
				<h2>Herramientas de Reportes</h2>
			</div>

			<div className="row">
				<div className="col-4">
					Herramienta:
					<select className="form-control" value={herramientaKey} onChange={handleSeleccionar}>
						<option value="">Selecciona una herramienta</option>
						{OPERACIONES.map((op) => (
							<option key={op.key} value={op.key}>
								{op.nombre}
							</option>
						))}
					</select>
				</div>

				{herramienta?.campos.map((campo) => (
					<div className="col-4" key={campo.name}>
						{campo.label}
						{campo.tipo === "ciclo" ? (
							<select
								name={campo.name}
								className="form-control"
								value={formValues[campo.name] || ""}
								onChange={handleInputChange}
							>
								<option value="">Seleccionar ciclo</option>
								{CICLOS.map((ciclo) => (
									<option key={ciclo} value={ciclo}>
										{ciclo}
									</option>
								))}
							</select>
						) : (
							<input
								type={campo.tipo === "number" ? "number" : "text"}
								name={campo.name}
								className="form-control"
								value={formValues[campo.name] || ""}
								onChange={handleInputChange}
							/>
						)}
					</div>
				))}
			</div>

			{herramienta && (
				<div className="row mt-3">
					<div className="col-4 d-flex align-items-center">
						<button
							className="btn btn-primary"
							onClick={handleEjecutar}
							disabled={loading || !camposCompletos}
						>
							Ejecutar
						</button>
						{loading && (
							<div className="spinner-border text-primary align-self-center ml-2" role="status">
								<span className="sr-only">Cargando...</span>
							</div>
						)}
					</div>
				</div>
			)}

			{resultado && resultado.length > 0 && (
				<div className="row mt-3">
					<div className="col-12 table-responsive">
						<table className="table table-sm">
							<thead>
								<tr>
									{Object.keys(resultado[0]).map((columna) => (
										<th key={columna}>{columna}</th>
									))}
								</tr>
							</thead>
							<tbody>
								{resultado.map((fila, indice) => (
									<tr key={indice}>
										{Object.keys(resultado[0]).map((columna) => (
											<td key={columna}>{String(fila[columna] ?? "")}</td>
										))}
									</tr>
								))}
							</tbody>
						</table>
					</div>
				</div>
			)}
		</div>
	);
};
